import { Link } from "react-router-dom";
import { Briefcase, CalendarClock, ChevronRight, ListTodo, MapPin, MessageSquare } from "lucide-react";
import { ACTIVITY, HEARINGS, STATS } from "@/data/demo";
import { useAuth } from "@/lib/auth";
import { Card, PageHeader } from "@/components/common";
import { HearingCalendar } from "@/components/HearingCalendar";
import { cn } from "@/lib/utils";

const ICONS = { cases: Briefcase, hearings: CalendarClock, messages: MessageSquare, tasks: ListTodo };
const DOT = { gold: "bg-gold", navy: "bg-navy", green: "bg-emerald-500" };

const StatCard = ({ s, i }) => {
  const Icon = ICONS[s.key];
  return (
    <Card data-testid={`stat-${s.key}`} className={cn("p-4 sm:p-5 animate-fade-up", i === 0 && "bg-navy border-navy")} style={{ animationDelay: `${i * 60}ms` }}>
      <div className={cn("grid h-10 w-10 place-items-center rounded-xl", i === 0 ? "bg-gold text-white" : "bg-amber-50 text-gold")}><Icon size={19} /></div>
      <div className={cn("mt-4 font-heading text-3xl font-bold", i === 0 ? "text-white" : "text-navy")}>{s.value}</div>
      <div className={cn("mt-0.5 text-xs font-medium sm:text-sm", i === 0 ? "text-slate-300" : "text-slate-500")}>{s.label}</div>
    </Card>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`Good day, ${user.name}. Here's your practice at a glance.`} />
      <div data-testid="dashboard-summary-cards" className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {STATS.map((s, i) => <StatCard key={s.key} s={s} i={i} />)}
      </div>
      {user.user_type === "lawyer" && <HearingCalendar />}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Card className="min-w-0 p-5 lg:col-span-3">
          <h2 className="font-heading text-lg font-semibold text-navy">Today's hearings</h2>
          <div data-testid="today-hearings-list" className="mt-4 flex flex-col gap-3">
            {HEARINGS.map((h) => (
              <Link key={h.id} to={`/cases/${h.caseId}`} data-testid={`hearing-${h.id}`} className="group flex items-center gap-4 rounded-xl border border-slate-100 p-3.5 transition-colors hover:border-amber-200 hover:bg-amber-50/40">
                <div className="w-[68px] shrink-0 rounded-lg bg-navy py-2 text-center text-xs font-bold text-white">{h.time}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-navy">{h.title}</div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-slate-500"><MapPin size={12} className="shrink-0" /><span className="truncate">{h.court}</span></div>
                  <div className="mt-1 text-xs font-medium text-gold">{h.purpose}</div>
                </div>
                <ChevronRight size={18} className="shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </Card>
        <Card className="min-w-0 p-5 lg:col-span-2">
          <h2 className="font-heading text-lg font-semibold text-navy">Recent activity</h2>
          <ul data-testid="recent-activity-list" className="mt-4 flex flex-col gap-4">
            {ACTIVITY.map((a) => (
              <li key={a.id} className="flex gap-3">
                <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", DOT[a.tone])} />
                <div>
                  <div className="text-sm text-slate-700">{a.text}</div>
                  <div className="mt-0.5 text-xs text-slate-400">{a.time}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
